import { useState, useRef, useEffect } from "react";
import { usePresence } from "@/lib/presence-context";
import { Users } from "lucide-react";

const colorForUser = (userId = "") => {
  // Stable hash → hue. Same algorithm as ProjectChat avatars so colours match.
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 50%)`;
};

const initials = (name = "?") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

export const PresencePill = () => {
  const { online, selfConnectionId, connected } = usePresence();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const others = online.filter((u) => u.connection_id !== selfConnectionId);
  const count = online.length;
  const dotCls = connected ? "bg-emerald-500" : "bg-gray-400";

  return (
    <div className="relative" ref={ref} data-testid="presence-pill">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        title={connected ? `${count} online` : "Reconnecting…"}
        data-testid="presence-pill-btn"
      >
        <span className={`w-2 h-2 rounded-full ${dotCls} ${connected ? "animate-pulse" : ""}`} />
        <Users className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-xs font-semibold text-gray-700" data-testid="presence-count">
          {count}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 w-72 bg-[#0D1117] border border-[#1F2937] rounded-md shadow-lg z-50"
          data-testid="presence-popover"
        >
          <div className="px-3 py-2 border-b border-[#1F2937] text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold flex items-center justify-between">
            <span>Online now</span>
            <span className="text-gray-400">{count} active</span>
          </div>
          {count === 0 ? (
            <div className="px-3 py-6 text-xs text-gray-500 text-center" data-testid="presence-empty">
              You&apos;re the only one online right now.
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {online.map((u) => {
                const isSelf = u.connection_id === selfConnectionId;
                return (
                  <div
                    key={u.user_id}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#030712]"
                    data-testid={`presence-user-${u.user_id}`}
                  >
                    <div
                      className="w-7 h-7 rounded-full grid place-items-center text-white text-[10px] font-bold shrink-0"
                      style={{ background: colorForUser(u.user_id) }}
                    >
                      {initials(u.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-white truncate">
                        {u.name}{isSelf && <span className="text-gray-400 font-normal"> · you</span>}
                      </div>
                      <div className="text-[10px] text-gray-500 truncate">{u.current_context}</div>
                    </div>
                  </div>
                );
              })}
              {others.length === 0 && (
                <div className="px-3 py-3 text-[11px] text-gray-400 text-center border-t border-[#1F2937]">
                  No teammates online yet. Invite your team from Team &amp; Access.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PresencePill;
